import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './styles.css';
import NotFoundPage from './components/errors/NotFoundPage';
import ErrorBoundary from './components/errors/ErrorBoundary';

// Lazy load pages
const HomePage = lazy(() => import(/* webpackChunkName: "home-page" */ './pages/HomePage'));
const LoginPage = lazy(() => import(/* webpackChunkName: "login-page" */ './pages/Login/Login'));
const RegisterPage = lazy(() => import(/* webpackChunkName: "register-page" */ './pages/Register/Register'));
const LeadsPage = lazy(() => import(/* webpackChunkName: "leads-page" */ './pages/LeadsPage'));
const ForgetPasswordPage = lazy(() => import(/* webpackChunkName: "forget-password-page" */ './components/ForgetPassword/ForgetPassword'));
const PasswordResetFormPage = lazy(() => import(/* webpackChunkName: "password-reset-form" */ './pages/ForgetPassword/PasswordResetForm'));
const PasswordResetCompletePage = lazy(() => import(/* webpackChunkName: "password-reset-complete" */ './components/ForgetPassword/pwd-reset-completed'));
const WidgetSettingsPage = lazy(() => import(/* webpackChunkName: "widget-settings-page" */ './components/widgetdashboard/WidgetSettingsPage'));
const SubscriptionSuccess = lazy(() => import(/* webpackChunkName: "subscription-success" */ './pages/subscription/SubscriptionSuccess'));
const SubscriptionLanding = lazy(() => import(/* webpackChunkName: "subscription-landing" */ './pages/subscription/SubscriptionLanding'));
const AffiliateReferral = lazy(() => import(/* webpackChunkName: "affiliate-referral" */ './pages/AffiliateReferral'));
const AffiliateRegister = lazy(() => import(/* webpackChunkName: "affiliate-register" */ './pages/AffiliateRegister'));


// Check if a JWT token is expired by decoding its payload
function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // exp is in seconds, Date.now() is in ms
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
}

// Protected route component - checks both authUser flag AND token validity
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = () => {
    const authUser = localStorage.getItem('authUser');
    if (!authUser) return false;
    try {
      const parsedUser = JSON.parse(authUser);
      if (!parsedUser || parsedUser.isAuthenticated !== true) return false;
    } catch {
      return false;
    }

    // Also check that we have a non-expired token (access or refresh)
    const accessToken = localStorage.getItem('authToken') || localStorage.getItem('access');
    const refreshToken = localStorage.getItem('refreshToken') || localStorage.getItem('refresh');

    if (!isTokenExpired(accessToken)) return true;  // access token still valid
    if (!isTokenExpired(refreshToken)) return true;  // refresh token still valid (will be refreshed on API call)

    // Both tokens expired — clear stale auth data
    localStorage.removeItem('authToken');
    localStorage.removeItem('access');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refresh');
    localStorage.removeItem('authUser');
    return false;
  };

  return isAuthenticated() ? children : <Navigate to="/login" state={{ message: 'Your session has expired. Please log in again.' }} />;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense
          fallback={
            <div style={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 12,
              color: '#0f172a'
            }}>
              <img src="/logo.png" alt="DreamRide" style={{ width: 72, height: 72, objectFit: 'contain' }} />
              <div style={{ fontSize: 18, fontWeight: 700 }}>DreamRide</div>
              <div style={{ fontSize: 13, opacity: 0.7 }}>Loading…</div>
            </div>
          }
        >
          <Routes>
          <Route
            path="/login"
            element={
                <LoginPage />
            }
          />
          <Route
            path="/register"
            element={
                <RegisterPage />
            }
          />
          <Route
            path="/register/affiliate"
            element={
                <AffiliateRegister />
            }
          />
          <Route
            path="/ref/:token"
            element={
                <AffiliateReferral />
            }
          />
          <Route
            path="/"
            element={
                <HomePage />
            }
          />
          <Route
            path="/forget-password"
            element={
                <ForgetPasswordPage />
            }
          />
          <Route
            path="/forget-password/reset/:uidb64/:token"
            element={
                <PasswordResetFormPage />
            }
          />
          <Route
            path="/forget-password-completed"
            element={
                <PasswordResetCompletePage />
            }
          />
          {/* Subscription success/cancel routes used by Stripe Checkout redirects */}
          <Route
            path="/account/subscription/success"
            element={<SubscriptionSuccess />}
          />
          <Route
            path="/account/subscription/"
            element={<SubscriptionLanding />}
          />
          {/* Protected Leads route */}
          <Route
            path="/leads/*"
            element={
                <ProtectedRoute>
                  <LeadsPage />
                </ProtectedRoute>
            }
          />
          {/* Nested routes under /leads handle widget settings and manage billing */}
          
          {/* 404 Not Found route */}
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;